import React from 'react';
import { Link } from 'react-router-dom';
import { Eyebrow } from './brand';

/**
 * Bloques SEO de la home (keyword principal: "software ganadero argentina").
 * Texto del documento "Optimización de contenido: home de MiCampoDigital"
 * (septiembre 2026), con los [CONFIRMAR] resueltos por el equipo: hoy todas las
 * funciones son gratis, hay roles por persona y la app genera el TXT del SIGSA.
 * Reglas: solo se SUMA texto (lo existente no se reescribe), voseo, sin guiones
 * largos, cada dato normativo con su fuente enlazada.
 */

const FUENTE_841 =
  'https://cvpba.org/identificacion-electronica-obligatoria-puntos-clave-de-la-resolucion-841-20255/';

/** B1 · Definición directa (párrafo para AI Overview). Después de los pilares. */
export const BloqueSoftwareGanadero: React.FC = () => (
  <section id="software-ganadero" className="lp-software lp-pad">
    <div className="lp-wrap">
      <div className="lp-sec-h lp-rv">
        <Eyebrow>Software ganadero</Eyebrow>
        <h2 className="lp-h2">Un software ganadero hecho para el campo argentino.</h2>
      </div>
      <div className="lp-prosa lp-rv lp-d1">
        <p>
          Un software ganadero es un programa para registrar lo que pasa con cada animal del rodeo (pesadas, tactos,
          vacunas, movimientos y gastos) y convertir esos registros en indicadores para decidir: ganancia diaria, tasa
          de preñez, costo por kilo y resultado del campo. Reemplaza el cuaderno de la manga y la planilla que se pasa a
          mano los domingos.
        </p>
        <p>
          MiCampoDigital es un software ganadero pensado para productores de Argentina. Se usa desde el celular o la
          compu, carga datos aunque no haya señal y habla como se habla en la manga: rodeo, tacto, parición, cuadro,
          caravana. Crear la cuenta es gratis y no pide tarjeta.
        </p>
        <p>
          La diferencia con un programa de escritorio o con una planilla está en dónde nace el dato. Acá nace en el
          corral, en el momento, leído de la caravana y de la balanza. Nadie tiene que pasarlo después, y por eso los
          números del mes siguiente son confiables. Mirá <Link to="/funcionalidades">todas las funcionalidades</Link>.
        </p>
      </div>
    </div>
  </section>
);

/** B2 · Por tipo de campo. Dentro de "Cómo funciona", antes de "En la manga". */
export const BloqueTiposDeCampo: React.FC = () => (
  <div id="tipos-de-campo" className="lp-tipos">
    <div className="lp-sec-h lp-rv">
      <h2 className="lp-h2">Cría, recría, invernada o corral: cada campo mira sus números.</h2>
      <p className="lp-lead">
        No todos los establecimientos se manejan igual, y un software ganadero tiene que mostrarle a cada uno lo que le
        importa. Estos son los números que MiCampoDigital pone adelante según cómo trabajás:
      </p>
    </div>
    <div className="lp-steps">
      <div className="lp-step lp-rv">
        <h3>Cría</h3>
        <p>
          El negocio se define en el tacto. La app guarda el resultado de cada tacto por caravana, calcula la tasa de
          preñez y te separa las vacías por lote. Con dos o tres temporadas cargadas, aparecen solas las vacas que fallan
          una y otra vez, que son las primeras candidatas a refugo. Mirá{' '}
          <Link to="/blog/control-de-prenez-paso-a-paso">cómo medir la preñez paso a paso</Link>.
        </p>
      </div>
      <div className="lp-step lp-rv lp-d1">
        <h3>Recría e invernada</h3>
        <p>
          Acá manda el kilo. Cada pesada queda asociada al animal, así que la ganancia diaria se calcula por animal, por
          categoría y por potrero. Si un lote viene más lento que otro en el mismo campo, lo ves en la pantalla antes de
          que se note en la balanza del camión.
        </p>
      </div>
      <div className="lp-step lp-rv lp-d2">
        <h3>Engorde a corral</h3>
        <p>
          En el corral, cada día de más cuesta plata. La <Link to="/calculadora-racion">calculadora de ración</Link>{' '}
          estima consumo, proteína y energía según el peso de tus animales, y la{' '}
          <Link to="/calculadora-engorde">calculadora de engorde</Link> te da el costo por kilo ganado con{' '}
          <Link to="/precios">precios de referencia del Mercado de Cañuelas</Link>. Las dos se pueden usar sin crear
          cuenta.
        </p>
      </div>
    </div>
  </div>
);

/** B3 · Caravana electrónica obligatoria (H3). Dentro de "En la manga", después de los chips. */
export const BloqueSenasa: React.FC = () => (
  <div className="lp-senasa">
    <h3>Caravana electrónica obligatoria: qué pide el SENASA desde 2026</h3>
    <p>
      Desde el 1 de enero de 2026, la{' '}
      <a href={FUENTE_841} target="_blank" rel="noopener noreferrer">
        Resolución 841/2025 del SENASA
      </a>{' '}
      establece que ningún ternero bovino o bubalino puede moverse ni quedarse en su campo de nacimiento después del
      destete sin su identificación electrónica oficial. El dispositivo va junto con la caravana visual en la oreja
      izquierda y se declara dentro de los 10 días hábiles de colocado, por SIGSA, en la oficina local o con la app
      SIGBIOTRAZA.
    </p>
    <p>
      Para el productor, eso significa que cada animal ya tiene un número electrónico único. MiCampoDigital lee ese
      número con el lector EID/RFID en la manga y le cuelga todo lo demás: pesadas, tactos, vacunas y movimientos. La
      caravana deja de ser solo un trámite y pasa a ser la llave de la historia de cada animal. Y a la hora de declarar,
      la app arma el archivo para subir al SIGSA y te marca qué caravanas ya declaraste y cuáles faltan.
    </p>
  </div>
);

const COMPARATIVA: { fila: string; cuaderno: string; excel: string; app: string }[] = [
  { fila: 'Dónde se carga', cuaderno: 'En la manga, a mano', excel: 'En la compu, después', app: 'En la manga, desde el celular' },
  { fila: 'Sin señal', cuaderno: 'Sí', excel: 'No aplica', app: 'Sí, se sincroniza al volver' },
  { fila: 'Caravana y balanza', cuaderno: 'Se anotan a mano', excel: 'Se tipean', app: 'Entran solas por lector' },
  { fila: 'Ganancia diaria y preñez', cuaderno: 'Hay que calcularlas', excel: 'Con fórmulas propias', app: 'Automáticas por lote' },
  { fila: 'Varios usuarios', cuaderno: 'Un solo cuaderno', excel: 'Versiones que se pisan', app: 'Todo el equipo ve lo mismo' },
  { fila: 'Costo', cuaderno: 'El cuaderno', excel: 'Licencia de Office', app: 'Crear la cuenta es gratis' },
];

/** B4 · Tabla comparativa (fragmento destacado). Después de "Traé tu Excel", antes del asistente. */
export const BloqueComparativa: React.FC = () => (
  <section id="cuaderno-excel-software" className="lp-comparativa lp-pad">
    <div className="lp-wrap">
      <div className="lp-sec-h lp-rv">
        <Eyebrow>Antes de cambiar</Eyebrow>
        <h2 className="lp-h2">Cuaderno, Excel o software ganadero: qué cambia.</h2>
        <p className="lp-lead">
          La mayoría de los campos ya lleva registros. La pregunta es cuánto trabajo cuesta sacarles un número. Esta
          comparación es la que hacen casi todos los productores antes de cambiar:
        </p>
      </div>
      <div className="lp-tabla-wrap lp-rv lp-d1">
        <table className="lp-tabla">
          <thead>
            <tr>
              <th scope="col">
                <span className="lp-sr">Qué se compara</span>
              </th>
              <th scope="col">Cuaderno</th>
              <th scope="col">Planilla de Excel</th>
              <th scope="col">MiCampoDigital</th>
            </tr>
          </thead>
          <tbody>
            {COMPARATIVA.map((r) => (
              <tr key={r.fila}>
                <th scope="row">{r.fila}</th>
                <td>{r.cuaderno}</td>
                <td>{r.excel}</td>
                <td>{r.app}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lp-prosa lp-rv">
        <p>
          Si hoy tenés todo en Excel, no empezás de cero: subís la planilla como está y el asistente propone qué columna
          es cada dato. Tus datos siguen siendo tuyos y los exportás cuando quieras. Y si querés hacer la cuenta del
          engorde a mano, acá está <Link to="/blog/como-calcular-costo-de-engorde">cómo calcular el costo de engorde</Link>.
        </p>
      </div>
    </div>
  </section>
);

/** B5 · Qué es gratis. Dentro de "Sin crear cuenta", antes de las tarjetas. */
export const BloqueGratis: React.FC = () => (
  <div className="lp-gratis-info lp-rv">
    <h3>Software ganadero gratis: qué incluye</h3>
    <p>
      Crear la cuenta es gratis y no te pedimos tarjeta. Hoy todas las funciones de la app están incluidas en la cuenta
      gratis: registro por caravana, lector y balanza, preñez y sanidad, gastos, reportes, mapa del campo y asistente.
      Las cuatro herramientas de abajo son gratis incluso sin cuenta: precio de la hacienda, calculadora de engorde,
      calculadora de ración y valor del inventario. Las usa cualquiera que quiera hacer números rápido, tenga o no su
      rodeo cargado.
    </p>
  </div>
);
